//@ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  format,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  parseISO,
} from "date-fns";
import { useParams } from "react-router-dom";
import { _baseRequest, COMMON } from "../../../utils/baseApi";
import { fetchJobPipelineDetailsData } from "../../../features/hiringpipeline/hiringPipelineSlice";
import { useAppDispatch } from "../../../app/hooks";
import { getColorForLabel } from "../../../utils/colorUtils";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import LoadingBar from "react-top-loading-bar";

const InboxDetails = [
  { id: "all", name: "All" },
  { id: "scheduled", name: "Scheduled" },
  { id: "done", name: "Done" },
  { id: "cancelled", name: "Cancelled" },
  { id: "pending", name: "Pending" },
];

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekDates, setWeekDates] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { baseId, jobId } = useParams<{ baseId: string; jobId: string }>();
  const userDetailsData = localStorage.getItem("jobByHiringTeamMembers");
  const baseMembers = userDetailsData ? JSON.parse(userDetailsData) : [];
  const dispatch = useAppDispatch();
  const [jobTeamMembers, setJobTeamMembers] = useState([]);
  const [selected, setSelected] = useState(InboxDetails[0]);
  const loadingBarRef = useRef(null);

  // Calculate week range (Monday to Sunday) whenever currentDate changes
  useEffect(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });

    // Get all dates in the current week
    const daysInWeek = eachDayOfInterval({ start, end });
    setWeekDates(daysInWeek);
  }, [currentDate]);

  // Navigate to next week
  const goToNextWeek = () => {
    setCurrentDate(addDays(currentDate, 7));
  };

  // Navigate to previous week
  const goToPrevWeek = () => {
    setCurrentDate(subDays(currentDate, 7));
  };

  // Go to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Sample events data (in a real app, this would come from props or API)

  // Fetch meetings data from API
  useEffect(() => {
    const fetchInitialData = async () => {
      // Format dates for API request
      const fromDate = format(
        weekDates.length > 0 ? weekDates[0] : currentDate,
        "yyyy-MM-dd",
      );
      const tillDate = format(
        weekDates.length > 0 ? weekDates[6] : addDays(currentDate, 6),
        "yyyy-MM-dd",
      );

      let fullUrl = `${COMMON.apiBaseUrl()}/v1/${baseId}/appMeeting.calendarList?from=${fromDate}&till=${tillDate}`;

      if (jobId) {
        fullUrl += `&job_id=${jobId}`;
      }

      // select
      if (selected.id === "scheduled") {
        fullUrl += `&current_state=scheduled`;
      } else if (selected.id === "done") {
        fullUrl += `&current_state=done`;
      } else if (selected.id === "cancelled") {
        fullUrl += `&current_state=cancelled`;
      } else if (selected.id === "pending") {
        fullUrl += `&current_state=pending`;
      }

      try {
        setIsLoading(true);
        loadingBarRef.current?.continuousStart();
        const response = await _baseRequest(fullUrl, "GET");

        // Process the API response and convert to events format
        if (response && response.data) {
          const formattedEvents = [];

          response.data.forEach((dateEntry) => {
            if (dateEntry.meetings && dateEntry.meetings.length > 0) {
              dateEntry.meetings.forEach((meeting) => {
                // Determine event color based on meeting properties (customize as needed)
                let status = meeting.current_state;

                // Format start and end times
                const startTime = meeting.from_time
                  ? meeting.from_time.substring(0, 5)
                  : "";
                const endTime = meeting.till_time
                  ? meeting.till_time.substring(0, 5)
                  : "";

                formattedEvents.push({
                  id: meeting.id,
                  title: meeting.details,
                  date: parseISO(meeting.date),
                  startTime: startTime,
                  endTime: endTime,
                  allDay: !startTime || !endTime,
                  location: meeting.location || "",
                  locationDetails: meeting.location_details,
                  timeZone: meeting.timezone,
                  hosts: meeting.hosts,
                  organiser: meeting.organiser,
                  // description: `Job ID: ${meeting.job_id}, App ID: ${meeting.app_id}`,
                  status: status,
                });
              });
            }
          });

          setEvents(formattedEvents);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setIsLoading(false);
        loadingBarRef.current?.complete();
      }
    };

    // Only fetch if weekDates are available and baseId is defined
    if (weekDates.length > 0 && typeof baseId !== "undefined") {
      fetchInitialData();
    }
  }, [weekDates, baseId, selected]);

  useEffect(() => {
    if (baseId) {
      const fetchTeams = async () => {
        const storedJobTeamMembers = JSON.parse(
          localStorage.getItem("jobByHiringTeamMembers") || "[]",
        );
        if (storedJobTeamMembers) {
          setJobTeamMembers(storedJobTeamMembers);
        } else {
          try {
            const baseResponse = await dispatch(
              fetchJobPipelineDetailsData({
                baseId: parseInt(baseId),
                JobId: jobId,
              }),
            );
            setJobTeamMembers(baseResponse.payload.data.teamMember);
          } catch (error) {
            console.error("Error fetching base member details:", error);
          }
        }
      };
      fetchTeams();
    }
  }, [baseId, dispatch]);

  // Find participants from baseMembers using user_ids from jobTeamMembers
  const availableParticipants = useMemo(() => {
    return jobTeamMembers
      .map((teamMember) => {
        const baseMember = baseMembers.find(
          (member) => member.id === teamMember.user_id,
        );

        return {
          id: teamMember.user_id,
          name: teamMember.name,
          image: teamMember?.profile_pic,
        };
      })
      .filter((participant) => participant.name);
  }, [jobTeamMembers]);

  // Add this inside your Calendar component
  const getHostsData = (hostIds) => {
    if (!hostIds || hostIds.length === 0) return [];

    const hostIdsArray =
      typeof hostIds === "string"
        ? hostIds.split(",").map((id) => id.trim())
        : Array.isArray(hostIds)
          ? hostIds
          : [hostIds];

    return hostIdsArray.map((hostId) => {
      const participant = availableParticipants.find((p) => p.id == hostId);
      return {
        id: hostId,
        name: participant ? participant.name : `Unknown (ID: ${hostId})`,
        image: participant?.image,
      };
    });
  };

  // Filter events for a specific date
  const getEventsForDate = (date) => {
    return events.filter((event) => isSameDay(event.date, date));
  };

  // Define color classes for different event statuses
  const getStatusColorClass = (status) => {
    const statusColors = {
      pending: "bg-gray-200/50 hover:bg-gray-200/40 text-gray-950/80",
      done: "bg-green-200/50 hover:bg-green-200/40 text-green-950/80",
      scheduled: "bg-sky-200/50 hover:bg-sky-200/40 text-sky-950/80",
      cancelled: "bg-red-200/50 hover:bg-red-200/40 text-red-950/80",
    };

    return statusColors[status] || statusColors.scheduled;
  };

  const getSenderDetails = (item) => {
    // Default return object
    const defaultDetails = {
      image: null,
      name: null,
    };

    // If msg_user_id exists, find in baseMembers
    if (item.organiser) {
      const senderUser =
        baseMembers.find((member) => member.user_id === item.organiser) || {};

      return {
        image: senderUser.profile_pic || null,
        name: senderUser.name || null,
      };
    }

    return defaultDetails;
  };

  const handleSelectionChange = (item) => {
    setSelected(item);
  };

  return (
    <div className="mt-[0px] h-[calc(100vh_-_62px)]  overflow-y-auto py-[32px] ring-1 lg:ring-zinc-950/5 shadow-xs rounded-tl-none rounded-tr-lg rounded-bl-none rounded-br-lg p-4">
      <LoadingBar color="#ff591e" ref={loadingBarRef} />
      <div className="overflow-hidden w-full max-w-[710px] mx-auto">
        <h2 className="text-lg font-medium text-gray-900">Calender</h2>
        <div className="bg-[#e0e0e0] w-full h-[1px] mb-[34px] mt-7"></div>
        <div className="-m-px grid grid-cols-12  *:not-first:-ms-px *:not-first:-mt-px">
          <div className="group/item relative border-t-0 border has-[[data-comp-loading=true]]:border-none col-span-12 sm:col-span-12 lg:col-span-12">
            <div className="flex flex-1 flex-col rounded-lg border">
              <div className="flex items-center justify-between p-2 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-4">
                  <span
                    className="table-tooltip before:!bottom-[24px] before:!left-[28px]"
                    data-title="today"
                  >
                    <button
                      onClick={goToToday}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] border border-input bg-white shadow-xs hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 aspect-square max-[479px]:p-0!"
                    >
                      <span className="max-[479px]:sr-only">Today</span>
                    </button>
                  </span>
                  <div className="flex gap-2">
                    <span
                      className="table-tooltip before:!bottom-[23px] before:!left-[18px]"
                      data-title="Prev"
                    >
                      <button
                        onClick={goToPrevWeek}
                        className="border-none rounded py-2 px-2 hover:bg-[#f5f4f4]"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={24}
                          height={24}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-left w-[1rem] h-[1rem] text-[#808080]"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M15 6l-6 6l6 6" />
                        </svg>
                      </button>
                    </span>
                    <span
                      className="table-tooltip before:!bottom-[23px] before:!left-[18px]"
                      data-title="Next"
                    >
                      <button
                        onClick={goToNextWeek}
                        className="border-none rounded py-2 px-2 hover:bg-[#f5f4f4]"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={24}
                          height={24}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-right w-[1rem] h-[1rem] text-[#808080]"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M9 6l6 6l-6 6" />
                        </svg>
                      </button>
                    </span>
                  </div>

                  <span className="text-sm text-gray-500">
                    {weekDates.length > 0 && (
                      <>
                        {format(weekDates[0], "d MMM")} -{" "}
                        {format(weekDates[6], "d MMM")}
                      </>
                    )}
                  </span>
                </div>
                <div className="flex flex-1 justify-end">
                  <Listbox value={selected} onChange={handleSelectionChange}>
                    <div className="relative z-10">
                      <ListboxButton
                        className="relative w-[116px] rounded-md bg-white py-1.5 px-[10px] text-left text-gray-900
          focus:outline-none  text-[14px] sm:leading-4 border border-solid border-[#e7e7e8] font-medium cursor-pointer"
                      >
                        <div className="flex items-center gap-1.5 justify-between">
                          <span className="block truncate">
                            {selected.name}
                          </span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="size-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m19.5 8.25-7.5 7.5-7.5-7.5"
                            />
                          </svg>
                        </div>
                      </ListboxButton>
                      <ListboxOptions
                        className="absolute z-10 mt-[9px] left-[0px] bg-white max-w-[116px] min-w-[116px] w-full overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg shadow-black/5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 
          "
                      >
                        {InboxDetails.map((item) => (
                          <ListboxOption
                            key={item.id}
                            value={item}
                            className={({ active }) =>
                              classNames(
                                active
                                  ? "bg-gray-200 text-[#1d2025]"
                                  : "text-gray-900",
                                "relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                              )
                            }
                          >
                            <span className="block truncate font-normal">
                              {item.name}
                            </span>
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </div>
                  </Listbox>
                </div>
              </div>
              <div className="flex flex-1 flex-col">
                <div className="border-t px-4">
                  {weekDates.map((date) => {
                    const dayEvents = getEventsForDate(date);
                    const isToday = isSameDay(date, new Date());
                    return (
                      <div
                        key={format(date, "yyyy-MM-dd")}
                        className="relative my-12 border-t"
                      >
                        <span
                          className={`bg-white absolute -top-3 left-0 flex h-6 items-center pe-4  text-sm text-[#374151] font-medium leading-5 capitalize ${isToday ? "font-medium" : ""} sm:text-xs`}
                        >
                          {format(date, "d MMM")}, {format(date, "EEEE")}
                        </span>
                        <div className="mt-6 space-y-2">
                          {dayEvents.length > 0 ? (
                            dayEvents.map((event) => {
                              const statusColorClass = getStatusColorClass(
                                event.status,
                              );
                              const { image, name } = getSenderDetails(event);

                              return (
                                <button
                                  key={event.id}
                                  className={`flex w-full flex-col gap-[6px] rounded-md p-3 text-left transition ${statusColorClass}`}
                                >
                                  <div className="text-sm font-medium leading-4 line-clamp-1 ">
                                    {event.title}
                                  </div>
                                  <div className="text-xs opacity-70  flex items-center gap-x-1 flex-wrap">
                                    {event.allDay ? (
                                      <span></span>
                                    ) : (
                                      <>
                                        <span className="text-xs leading-4 uppercase">
                                          {event.startTime} - {event.endTime}
                                        </span>
                                        {event.location && (
                                          <>
                                            <span className="mx-[6px] w-[1px] h-[12px] bg-gray-400 block">
                                              {" "}
                                            </span>
                                            <span className=" capitalize">
                                              {event.location}
                                            </span>
                                          </>
                                        )}
                                        {event.locationDetails && (
                                          <>
                                            <span className="mx-[6px] w-[1px] h-[12px] bg-gray-400 block">
                                              {" "}
                                            </span>
                                            <span className="">
                                              {event.locationDetails}
                                            </span>
                                          </>
                                        )}
                                        {event.timeZone && (
                                          <>
                                            <span className="mx-[6px] w-[1px] h-[12px] bg-gray-400 block">
                                              {" "}
                                            </span>
                                            <span>{event.timeZone}</span>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-x-1">
                                    {name && (
                                      <div className="flex items-center gap-x-2">
                                        {image ? (
                                          <img
                                            src={`${process.env.REACT_APP_API_BASE_URL}/${image}`}
                                            alt={`${"Profile"}`}
                                            className="h-4 w-4 flex-none rounded-full ring-1 ring-gray-900/10"
                                          />
                                        ) : (
                                          <div
                                            style={{
                                              backgroundColor: getColorForLabel(
                                                name?.charAt(0),
                                              ),
                                            }}
                                            className="w-4 h-4 rounded-full flex items-center justify-center"
                                          >
                                            <span className="font-medium text-white mt-[0px] leading-4">
                                              {name?.charAt(0)}
                                            </span>
                                          </div>
                                        )}
                                        {name && (
                                          <p className="text-xs opacity-70 capitalize">
                                            {name}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                    {event.hosts && event.hosts.length > 0 && (
                                      <div className="flex items-center gap-2">
                                        <span className="mx-[6px] w-[1px] h-[12px] bg-gray-400 block">
                                          {" "}
                                        </span>
                                        <span className="text-xs opacity-70">
                                          Hosts:
                                        </span>
                                        <div className="flex flex-wrap gap-2 items-center">
                                          {getHostsData(event.hosts).map(
                                            (host) => (
                                              <div
                                                key={host.id}
                                                className="flex items-center gap-2"
                                              >
                                                {host.image ? (
                                                  <img
                                                    src={`${process.env.REACT_APP_API_BASE_URL}/${host.image}`}
                                                    alt={`${"host.name"}`}
                                                    className="h-4 w-4 flex-none rounded-full ring-1 ring-gray-900/10"
                                                  />
                                                ) : (
                                                  <div
                                                    style={{
                                                      backgroundColor:
                                                        getColorForLabel(
                                                          host.name?.charAt(0),
                                                        ),
                                                    }}
                                                    className="w-4 h-4 rounded-full flex items-center justify-center"
                                                  >
                                                    <span className="font-medium text-white mt-[0px] leading-4 text-sm">
                                                      {host.name?.charAt(0)}
                                                    </span>
                                                  </div>
                                                )}
                                                <span className="text-xs opacity-70 capitalize">
                                                  {host.name}
                                                </span>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </button>
                              );
                            })
                          ) : (
                            <div className="text-sm text-gray-400">
                              No events scheduled
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Calendar;
